import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLeadLists } from "@/hooks/use-lead-lists";
import { useAdminLeadAction } from "@/hooks/use-lead-pools";
import { useLeadQueueOverview } from "@/hooks/use-lead-queue";
import { NotRelevantDialog } from "./LeadOutcomeDialogs";

interface Props {
  leadIds: string[];
  /** Poolen leadene ligger i, så den ikke tilbys som mål for «Flytt til pool». */
  currentListId: string;
  onDone?: () => void;
}

/**
 * Handlingene for admin på ett eller flere leads (VIB-3050 skjerm 5 og 6): tildel til en booker,
 * tilbake til poolen, flytt til en annen pool eller avvis. Alt logges per lead med deg som aktør.
 */
export default function LeadAdminActions({ leadIds, currentListId, onDone }: Props) {
  const act = useAdminLeadAction();
  const { data: bookers = [] } = useLeadQueueOverview();
  const { data: lists = [] } = useLeadLists();
  const [rejecting, setRejecting] = useState(false);
  const disabled = leadIds.length === 0 || act.isPending;
  const run = (a: Parameters<typeof act.mutate>[0]) => act.mutate(a, { onSuccess: () => onDone?.() });

  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Handlinger">
      <Select value="" disabled={disabled} onValueChange={(userId) => run({ leadIds, action: "assign", userId })}>
        <SelectTrigger className="h-9 w-44" aria-label="Tildel til">
          <SelectValue placeholder="Tildel til …" />
        </SelectTrigger>
        <SelectContent>
          {bookers.map((b) => (
            <SelectItem key={b.user_id} value={b.user_id}>{b.full_name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="outline" size="sm" disabled={disabled} onClick={() => run({ leadIds, action: "return" })}>
        Tilbake til poolen
      </Button>
      <Select value="" disabled={disabled} onValueChange={(listId) => run({ leadIds, action: "move", listId })}>
        <SelectTrigger className="h-9 w-48" aria-label="Flytt til pool">
          <SelectValue placeholder="Flytt til pool …" />
        </SelectTrigger>
        <SelectContent>
          {lists.filter((l) => l.id !== currentListId).map((l) => (
            <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button variant="destructive" size="sm" disabled={disabled} onClick={() => setRejecting(true)}>
        Avvist
      </Button>
      <NotRelevantDialog
        open={rejecting}
        onOpenChange={setRejecting}
        pending={act.isPending}
        onSubmit={({ reason, revisitOn }) => {
          setRejecting(false);
          run({ leadIds, action: "reject", reason, revisitOn });
        }}
      />
    </div>
  );
}
